import { useQuery, useMutation } from '@apollo/client'
import {
  Button,
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Tooltip,
} from '@mui/material'
import { ColumnsType } from 'antd/es/table'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import Card from '../../Components/Cards/Card'
import DataTable from '../../Components/Datatable'
import Stats from '../../Components/Stats'
import TableHeading from '../../Components/TableHeading'
import globals from '../../Constants/Globals'
import {
  GET_ALL_COHORTS,
  GET_USER_BY_ID,
  GET_COHORT_BY_ID,
} from '../../Graphql/Queries'
import {
  BATCH_UPLOAD,
  DELETE_COHORT,
  BATCH_REPORT,
} from '../../Graphql/Mutations'
import { AdminPaths } from '../../Routes/Paths'
import { pages } from '../../Types/DatatableType'
import DeleteIcon from '@mui/icons-material/Delete'
import Message from '../../Components/Message'
import { Error } from '../../Types/MessageType'
import { BATCH_UPLOAD_SUBSCRIPTION } from '../../Graphql/Subscriptions'
import SubscriptionFileUploader from '../../Components/SubscriptionFileUploader'
import { AdminPermissions } from '../../Constants/Permissions'
import useUserPermissions from '../../hooks/useUserPermission'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'; // Import the View Details icon
import IosShareIcon from '@mui/icons-material/IosShare'; // Import the Export Analytics icon
import NoteAddIcon from '@mui/icons-material/NoteAdd'; // Import the Create icon
import UploadFileIcon from '@mui/icons-material/UploadFile';

const truncateText = (text: string, wordLimit: number) => {
  if (!text) return '';
  const words = text.split(' ');
  if (words.length > wordLimit) {
    return words.slice(0, wordLimit).join(' ') + '...';
  }
  return text;
};

const Cohort = () => {
  const { CREATE_COHORT_PATH, BATCH_UPLOAD_PATH } = AdminPaths
  const navigate = useNavigate()
  const [tableData, setTableData] = useState<any[]>([])
  const [totalPages, setTotalPages] = useState<pages>()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [isRootAdmin, setIsRootAdmin] = useState<boolean>(false)
  const [trainingPackageId, setTrainingPackageId] = useState<string | null>(
    null
  )
  const [searchParams, setSearchParams] = useState({
    cohortName: '',
    batchDescription: '',
    startDate: '',
    numberOfUsers: 0,
    numberOfTrainings: 0,
  })
  const [alert, setAlert] = useState<Error>({ type: 'warning', message: '' })
  const [open, setOpen] = useState(false)
  const [record, setRecord] = useState<any>(null)

  const { VIEW_SAVE_COHORT, VIEW_DELETE_COHORT, VIEW_UPLOAD_BATCH } = AdminPermissions
  const { permissions } = useUserPermissions()
  const { userId, organization } = useSelector((state: any) => state.auth)

  const { data: userData, loading: userLoading } = useQuery(GET_USER_BY_ID, {
    variables: { userWithRolesByIdId: userId },
    onCompleted(data) {
      const roles = data?.userWithRolesById?.roles || []
      const isAdmin = roles.some((role: any) => role.roleCode === 'ROOT_ADMIN')
      setIsRootAdmin(isAdmin)
      const orgId = data?.userWithRolesById?.organization || null
      setTrainingPackageId(orgId) // Assuming orgId is the training package ID, adjust if necessary
    },
    onError(err) {
      console.error('Error fetching user data:', err.message)
    },
  })

  // Query for root admin to fetch all cohorts
  const { loading: cohortsLoading } = useQuery(GET_ALL_COHORTS, {
    variables: {
      orgId: organization,
      search: searchParams,
    },
    onCompleted(data) {
      if (data.getAllCohorts.length > 0) {
        const cohorts = data?.getAllCohorts
        setTableData([])
        if (cohorts.length > 0) {
          const _cohorts = cohorts.map((_singleCohort: any, index: number) => {
            return {
              key: index,
              id: _singleCohort._id,
              cohortName: _singleCohort.cohortName,
              batchDescription: _singleCohort.batchDescription,
              startDate: _singleCohort.cohortStartDate,
              noOfUsers: _singleCohort.userIds,
              noOfTrainings: _singleCohort.cohortTrainings,
            }
          })
          setTableData(_cohorts)
        }
      }
    },
    onError(err) {
      console.log('Error fetching all cohorts:', err.message)
    },
  })

  const { loading: cohortLoading } = useQuery(GET_COHORT_BY_ID, {
    skip: isRootAdmin || !trainingPackageId,
    variables: { getCohortByIdId: trainingPackageId },
    onCompleted(data) {
      console.log('Cohort data fetched:', data)
      const cohort = data?.getCohortById
      if (cohort) {
        const _cohorts = [
          {
            key: 0,
            id: cohort._id,
            cohortName: cohort.cohortName,
            batchDescription: cohort.batchDescription,
            startDate: cohort.cohortStartDate,
            noOfUsers: cohort.userIds.length,
            noOfTrainings: cohort.cohortTrainings.length,
          },
        ]
        setTableData(_cohorts)
      }
    },
    onError(err) {
      console.log('Error fetching cohort:', err.message)
    },
  })

  const [deleteCohort] = useMutation(DELETE_COHORT)

  const handleClickOpen = (record: any) => {
    setRecord(record)
    setOpen(true)
  }

  const handleDelete = () => {
    handleDeleteCohort(record)
    setOpen(false)
    setRecord(null)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleDeleteCohort = (record: any): void => {
    deleteCohort({
      variables: {
        id: record.id,
      },
      onCompleted(data) {
        const updatedTableData = tableData.filter(
          (item) => item.id !== record.id
        )
        setTableData(updatedTableData)
        setAlert({ type: 'success', message: 'Batch deleted successfully' })
        setTimeout(() => {
          setAlert({ type: 'success', message: '' })
        }, globals.timeout)
      },
      onError(error) {
        setAlert({ type: 'error', message: error.message })
        setTimeout(() => {
          setAlert({ type: 'error', message: '' })
        }, globals.timeout)
      },
    })
  }

  const [exportBatchReport, { loading: exportLoading }] =
    useMutation(BATCH_REPORT)

  const handleExportAnalytics = (batchId: string) => {
    exportBatchReport({
      variables: {
        batchId: batchId,
      },
      onCompleted(data) {
        if (data?.generateBatchWiseReport) {
          const link = document.createElement('a')
          link.href = data.generateBatchWiseReport
          link.setAttribute('download', `batch-analytics-${batchId}.csv`)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          setAlert({
            type: 'success',
            message: 'Batch analytics exported successfully',
          })
          setTimeout(() => {
            setAlert({
              type: 'success',
              message: '',
            })
          }, globals.timeout)
        }
      },
      onError(error) {
        setAlert({ type: 'error', message: error.message })
        setTimeout(() => {
          setAlert({
            type: 'error',
            message: '',
          })
        }, globals.timeout)
      },
    })
  }

  const columns: ColumnsType<any> = [
    {
      title: (
        <TableHeading
          heading='Batch Name'
          setSearch={(value) =>
            setSearchParams((prev) => ({ ...prev, cohortName: value }))
          }
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'cohortName',
      render: (_: unknown, record: { cohortName: string }) => (
        <Tooltip title={record.cohortName} arrow>
          <Typography
            variant="body1"
            sx={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 300,
              display: "block",
            }}
          >
            {record.cohortName || "N/A"}
          </Typography>
        </Tooltip>
      ),
      key: '1',
      width: "23%",
    },
    {
      title: (
        <TableHeading
          heading='Batch Description'
          setSearch={(value) =>
            setSearchParams((prev) => ({ ...prev, batchDescription: value }))
          }
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'batchDescription',
      render: (_: unknown, record: { batchDescription: string }) => (
        <Tooltip title={record.batchDescription} arrow>
          <Typography
            variant="body1"
            sx={{
              textAlign: "left",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: 200,
              display: "block",
            }}
          >
            {record.batchDescription || "N/A"}
          </Typography>
        </Tooltip>
      ),
      key: '2',
      width: "23%"
    },
    {
      title: (
        <TableHeading
          heading='Start Date'
          setSearch={(value) =>
            setSearchParams((prev) => ({ ...prev, startDate: value }))
          }
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'startDate',
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
      key: '3',
      width: '14%',
    },
    {
      title: (
        <TableHeading
          heading='No of Users'
          setSearch={(value) =>
            setSearchParams((prev) => ({ 
              ...prev, 
              numberOfUsers: value ? parseInt(value, 10) : 0 
            }))
          }
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'noOfUsers',
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
      key: '4',
      width: '10%',
    },
    {
      title: (
        <TableHeading
          heading='No of Trainings'
          setSearch={(value) =>
            setSearchParams((prev) => ({ 
              ...prev, 
              numberOfTrainings: value ? parseInt(value, 10) : 0 
            }))
          }
          setPage={setCurrentPage}
        />
      ),
      dataIndex: 'noOfTrainings',
      render: (text) => (
        <Typography variant='body1' sx={{ textAlign: 'left' }}>
          {text}
        </Typography>
      ),
      key: '5',
      width: '12%',
    },
    {
      title: '',
      render: (_, record) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="View Details" arrow>
            <IconButton
              sx={{ color: "rgb(250, 150, 157)" }}
              onClick={() => navigate(record.id, { state: { cohortId: record.id } })}
            >
              <RemoveRedEyeIcon sx={{ fontSize: "1.8rem" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Analytics" arrow>
            <IconButton
              sx={{ color: "rgb(250, 150, 157)" ,    left: '15%'}}
              onClick={(e) => {
                e.stopPropagation(); handleExportAnalytics(record.id)}}
            >
              <IosShareIcon sx={{ fontSize: "1.8rem" }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      key: '6',
    },
    {
      title: '',
      render: (_, record) => (
        <IconButton
          sx={{ alignSelf: 'flex-end' }}
          onClick={(e) => {
            e.stopPropagation(); 
            handleClickOpen(record); 
          }}
        >
          <DeleteIcon />
        </IconButton>
      ),
      key: '7',
    },
  ]

  const loading =
    userLoading ||
    (isRootAdmin ? cohortsLoading : cohortLoading) ||
    exportLoading

  const buttons = [
    permissions?.includes(VIEW_SAVE_COHORT) && {
      text: (
        <>
          Create Batch <NoteAddIcon sx={{ color: 'white', ml: 1 }} />
        </>
      ),
      onClick: () => navigate(CREATE_COHORT_PATH),
    },
    permissions?.includes(VIEW_UPLOAD_BATCH) && {
      text: (
        <>
          Upload Batch <UploadFileIcon sx={{ color: 'white', ml: 1 }} />
        </>
      ),
      onClick: () => navigate(BATCH_UPLOAD_PATH),
    },
  ].filter(Boolean); // Remove `false` values from the array

  const handleRowClick = (record: any) => {
    navigate(`/batch/${record.id}`, { state: { cohortId: record.id } });
  };

  return (
    <>
      <Stats text='Total Batches' number={tableData.length} buttons={buttons} />

      <Box flexGrow={1} sx={{ pr: 3 }}>
        {!!alert?.message ? (
          <Message
            type={alert?.type}
            message={alert.message}
            variant={'filled'}
          />
        ) : null}
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            Are you sure you want to delete this batch?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Card>
        <DataTable
          loading={loading}
          tableData={tableData}
          totalPages={totalPages}
          showCheck={false}
          columnData={columns}
          page={(cp) => {
            setCurrentPage(Number(cp))
          }}
          scrollX='max-content'
          scrollY={450}
          onRow={(record) => handleRowClick(record)}
        />
      </Card>
    </>
  )
}

export default Cohort
